import { z } from 'zod';
import { daySummarySchema } from './log.js';
/* ==========================================================================
 *  Log utility wire contract (T11.2 / ISSUE-070)
 *
 *  Three deterministic, €0 friction-killers:
 *  1. POST /me/logs/copy   — copy a previous day or meal
 *  2. POST /me/logs/quick-add — add raw macros without pipeline
 *  3. GET  /me/recents    — ranked recents feed for the composer
 * ========================================================================== */
/* ── POST /me/logs/copy ─────────────────────────────────────────────────── */
/**
 * Copy a previous day's entries (scope='day') or a specific raw log's entries
 * (scope='mealSlot') into a target date. Unresolved (0-macro) entries are
 * silently skipped; counts are reported in the response.
 */
export const copyLogSchema = z
    .object({
    sourceDate: z.string().date(),
    targetDate: z.string().date(),
    /** 'day' clones all resolved entries for the source date;
     *  'mealSlot' clones entries from one specific raw_log. */
    scope: z.enum(['day', 'mealSlot']),
    /** rawLogId of the source log — required when scope = 'mealSlot'. */
    mealSlot: z.string().uuid().optional(),
})
    .refine((v) => !(v.scope === 'mealSlot' && !v.mealSlot), {
    message: 'mealSlot (rawLogId) is required when scope is mealSlot.',
});
export const copyLogResponseSchema = z.object({
    rawLogId: z.string().uuid(),
    copiedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    day: daySummarySchema,
});
/* ── POST /me/logs/quick-add ────────────────────────────────────────────── */
/**
 * Add a macro entry directly without running the NLP pipeline.
 * Creates a single user_direct meal_entry (resolvedBy='user_direct').
 * kcal-vs-macro sanity is warned but not blocked (additive tone).
 */
export const quickAddSchema = z.object({
    date: z.string().date(),
    /** HH:MM (UTC) — defaults to 12:00 when omitted. */
    time: z
        .string()
        .regex(/^\d{2}:\d{2}$/, 'time must be HH:MM')
        .optional(),
    label: z.string().min(1).max(200).optional(),
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative().optional(),
    carbsG: z.number().nonnegative().optional(),
    fatG: z.number().nonnegative().optional(),
    fiberG: z.number().nonnegative().optional(),
});
export const quickAddResponseSchema = z.object({
    rawLogId: z.string().uuid(),
    entryId: z.string().uuid(),
    /** Non-null when logged kcal diverges noticeably from macro totals. */
    warning: z.string().nullable(),
    day: daySummarySchema,
});
/* ── GET /me/recents ─────────────────────────────────────────────────────── */
/** A frequently-used or recently-logged food from the user's history. */
export const recentFoodSchema = z.object({
    type: z.literal('food'),
    name: z.string(),
    foodId: z.string().nullable(),
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    fiberG: z.number(),
    frequency: z.number().int().positive(),
    lastUsedAt: z.string().datetime(),
});
/** A saved meal that was recently applied — surfaced for 1-tap re-logging. */
export const recentSavedMealSchema = z.object({
    type: z.literal('saved_meal'),
    id: z.string().uuid(),
    name: z.string(),
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    fiberG: z.number(),
    frequency: z.number().int().positive(),
    lastUsedAt: z.string().datetime(),
});
export const recentItemSchema = z.discriminatedUnion('type', [
    recentFoodSchema,
    recentSavedMealSchema,
]);
export const recentsResponseSchema = z.object({
    items: z.array(recentItemSchema),
});
//# sourceMappingURL=log-utils.js.map